import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, Space} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetMeasurementUnitLabourOperationByIdQuery,
  usePatchMeasurementUnitLabourOperationMutation,
  usePostMeasurementUnitLabourOperationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {getOptionsFromAuthorizationProfiles, handleApiError, EMPTY_PLACEHOLDER} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  WorkshopMeasurementUnitForm,
  WorkshopMeasurementUnitFormSchema,
} from './types/WorkshopMeasurementUnitForm';

export function WorkshopMeasurementUnitDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data: tenant} = useGetTenantQuery();

  const isCreating = isNil(id);

  const {data, isLoading, isError} = useGetMeasurementUnitLabourOperationByIdQuery(
    {muloId: id ?? ''},
    {skip: isCreating}
  );
  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});
  const [postMeasurementUnitLabourOperation] = usePostMeasurementUnitLabourOperationMutation();
  const [patchMeasurementUnitLabourOperation] = usePatchMeasurementUnitLabourOperationMutation();

  const handleSubmit: FormSubmitHandler<WorkshopMeasurementUnitForm> = (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = isCreating
      ? postMeasurementUnitLabourOperation({body: data})
      : patchMeasurementUnitLabourOperation({muloId: id ?? '', body: data});

    return request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.workshopMeasurementUnits);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const titleKey = isCreating
    ? 'page.settings.labels.newMeasurementUnit'
    : 'page.settings.labels.measurementUnits';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: isCreating
            ? i18n.t('page.settings.labels.newMeasurementUnit')
            : (authorizationProfiles?.find((profile) => profile.id === data?.authorizationProfileId)
                ?.name ?? EMPTY_PLACEHOLDER),
          breadcrumbs: [
            {
              label: i18n.t('page.settings.labels.measurementUnits'),
              href: settingsRoutes.workshopMeasurementUnits,
            },
          ],
        }}
        isError={isError}
        isLoading={isLoading}
        isCreating={isCreating}
        data-testid={testIds.settings.workshopMeasurementUnitDetail('template')}
      >
        <Form<WorkshopMeasurementUnitForm>
          defaultValues={{
            mulo: data?.mulo,
            conversionRatio: data?.conversionRatio,
            authorizationProfileId: data?.authorizationProfileId,
          }}
          onSubmit={handleSubmit}
          experimentalZodSchema={WorkshopMeasurementUnitFormSchema}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <SettingsSection>
              <Grid columns={2} spacing={4}>
                <FormField
                  control={control}
                  name="authorizationProfileId"
                  type="choice"
                  isRequired
                  options={getOptionsFromAuthorizationProfiles(
                    authorizationProfiles,
                    formApi.watch('authorizationProfileId')
                  )}
                  isLoading={isAuthorizationProfilesLoading}
                  label={i18n.t('entity.settings.labels.authorizationProfile')}
                  data-testid={testIds.settings.workshopMeasurementUnitDetail(
                    'authorizationProfile'
                  )}
                />
                <Space vertical={6} />
                <Card title={i18n.t('entity.settings.labels.converter')} variant="inlineGrey">
                  <FormField
                    control={control}
                    name="mulo"
                    type="text"
                    isRequired
                    label={i18n.t('entity.settings.labels.measurementUnitOfLabourOperation')}
                    data-testid={testIds.settings.workshopMeasurementUnitDetail('mulo')}
                  />
                  <Space vertical={4} />
                  <FormField
                    control={control}
                    name="conversionRatio"
                    type="number"
                    isRequired
                    label={i18n.t('entity.settings.labels.coversionRatio')}
                    helperText={i18n.t('entity.settings.labels.coversionRatioHelperText')}
                    data-testid={testIds.settings.workshopMeasurementUnitDetail('conversionRatio')}
                  />
                </Card>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      variant: 'secondary',
                      title: i18n.t('general.actions.discard'),
                      onClick: () =>
                        navigateWithoutBlocker(settingsRoutes.workshopMeasurementUnits),
                    },
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: isCreating
                        ? i18n.t('general.actions.create')
                        : i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                />
              </Grid>
            </SettingsSection>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
